import type { ClientOngoingLoad, LoadNotesSnapshot } from '@numeo/types-core'
import { createAction, createSelector } from '@reduxjs/toolkit'
import type { RootState } from './index'

export const loadNotesReceived = createAction<LoadNotesSnapshot>('loads/notesReceived')

/** Old clients/snapshots have revision zero. Other load fields have independent update ordering. */
export const hasOlderLoadNotes = (base: Pick<ClientOngoingLoad, 'notesRevision'>, incoming: Partial<LoadNotesSnapshot>): boolean =>
    incoming.notes !== undefined && (incoming.notesRevision ?? 0) < (base.notesRevision ?? 0)

export const applyLoadNotesToBuckets = (buckets: ClientOngoingLoad[][], incoming: LoadNotesSnapshot): void => {
    for (const bucket of buckets) {
        for (const load of bucket) {
            if (load.id !== incoming.id || hasOlderLoadNotes(load, incoming)) continue
            load.notes = incoming.notes ?? []
            load.notesRevision = incoming.notesRevision ?? 0
        }
    }
}

/** Derived once per bucket change; lookups during a socket burst never rescan history per load. */
export const selectLoadUpdateIndex = createSelector(
    [
        (state: RootState) => state.loads.upcomingLoads,
        (state: RootState) => state.loads.dispatchedLoads,
        (state: RootState) => state.loads.inTransitLoads,
        (state: RootState) => state.loads.historyLoads,
        (state: RootState) => state.scheduler?.loads?.assigned,
        (state: RootState) => state.scheduler?.loads?.inTransit,
        (state: RootState) => state.scheduler?.loads?.upcoming,
        (state: RootState) => state.scheduler?.unassigned,
        (state: RootState) => state.scheduler?.selectedLoad,
    ],
    (...sources) => {
        const main = new Map<string, ClientOngoingLoad>()
        const all = new Map<string, ClientOngoingLoad>()
        const latestNotes = new Map<string, ClientOngoingLoad>()
        sources.forEach((source, bucketIndex) => {
            const add = (load: ClientOngoingLoad) => {
                if (!all.has(load.id)) all.set(load.id, load)
                if (bucketIndex < 4 && !main.has(load.id)) main.set(load.id, load)
                const current = latestNotes.get(load.id)
                if (!current || (load.notesRevision ?? 0) > (current.notesRevision ?? 0)) latestNotes.set(load.id, load)
            }
            if (Array.isArray(source)) source.forEach(add)
            else if (source) add(source)
        })
        return { main, all, latestNotes }
    }
)

export const selectLatestLoadNotesById = createSelector([selectLoadUpdateIndex], ({ latestNotes }) => latestNotes)

/** Rebase pending socket notes against HTTP results received during the debounce window. */
export const preserveCurrentLoadNotes = (load: ClientOngoingLoad, state: RootState): ClientOngoingLoad => {
    const current = selectLatestLoadNotesById(state).get(load.id)
    return current && (load.notesRevision ?? 0) < (current.notesRevision ?? 0) ? { ...load, notes: current.notes, notesRevision: current.notesRevision } : load
}
